import { apiUrl, buildQuery, requestJson } from './client'

export type SunoPromptBaseSnapshot = {
  id: string
  title: string
  description: string | null
  style_prompt: string
  exclude_prompt: string | null
}

export type TrackTitleLanguage = 'en' | 'auto' | 'ru'
export type SunoStylePromptGenerationMode = 'free' | 'unusual' | 'library_bound' | 'hybrid'

export type TrackTitleSuggestion = {
  text: string
}

export type TrackTitleSuggestionResult = {
  items: TrackTitleSuggestion[]
  meta: {
    model: string
    provider_request_id: string | null
    usage: {
      promptTokens: number | null
      completionTokens: number | null
      totalTokens: number | null
    }
  }
}

export type SunoStylePromptSuggestion = {
  title: string
  style_prompt: string
  exclude_prompt: string | null
  explanation: string
}

export type SunoStylePromptSuggestionResult = {
  items: SunoStylePromptSuggestion[]
  history_item: SunoStylePromptHistoryItem | null
  meta: {
    model: string
    provider_request_id: string | null
    usage: {
      promptTokens: number | null
      completionTokens: number | null
      totalTokens: number | null
    }
  }
}

export type SunoPromptMetadataSuggestionResult = {
  title: string
  description: string
  meta: {
    model: string
    provider_request_id: string | null
    usage: {
      promptTokens: number | null
      completionTokens: number | null
      totalTokens: number | null
    }
  }
}

export type SunoStylePromptHistoryItem = {
  id: string
  admin_user_id: string
  description: string
  generation_mode: SunoStylePromptGenerationMode
  result_json: SunoStylePromptSuggestion[]
  base_prompt_snapshot: SunoPromptBaseSnapshot | null
  created_at: string
}

export type SunoPrompt = {
  id: string
  title: string
  description: string | null
  style_prompt: string
  exclude_prompt: string | null
  groups: SunoPromptGroupSummary[]
  pool: SunoPromptPoolSummary | null
  pool_variant_count: number
  linked_track_count: number
  created_at: string
  updated_at: string
}

export type SunoPromptFilters = {
  group?: string | undefined
  page?: number | undefined
  pageSize?: number | undefined
  search?: string | undefined
  sortBy?: 'created_at' | 'groups' | 'linked_track_count' | 'title' | 'updated_at' | undefined
  sortDirection?: 'asc' | 'desc' | undefined
}

export type SunoPromptListResult = {
  items: SunoPrompt[]
  page: number
  pageSize: number
  total: number
}

export type SunoPromptGroupSummary = {
  id: string
  name: string
}

export type SunoPromptGroup = {
  id: string
  name: string
  description: string | null
  prompt_count: number
  created_at: string
  updated_at: string
}

export type SunoPromptPoolSummary = {
  id: string
  name: string
  canonical_suno_prompt_id: string
}

export type SunoPromptPoolVariant = {
  id: string
  prompt: SunoPrompt
  sort_order: number
  created_at: string
}

export type SunoPromptPool = {
  id: string
  name: string
  description: string | null
  canonical_suno_prompt_id: string
  canonical_prompt: SunoPrompt
  variants: SunoPromptPoolVariant[]
  created_at: string
  updated_at: string
}

export type SunoPromptSimilarityReason =
  | 'exact_match'
  | 'same_group_unpooled'
  | 'same_style_different_exclude'
  | 'similar_style'

export type SunoPromptSimilarityCandidate = {
  prompt: SunoPrompt
  reason: SunoPromptSimilarityReason
  score: number
  merge_pool: SunoPromptPoolSummary | null
}

export type SunoPromptSimilarityResult = {
  prompt: SunoPrompt
  items: SunoPromptSimilarityCandidate[]
}

export type SunoPromptTrackSummary = {
  id: string
  title: string
  artist: string | null
  style_prompt: string | null
  file_url: string
  duration_seconds: number | null
  status: string
  created_at: string
}

export type SunoPromptTrackBindingSummary = SunoPromptTrackSummary & {
  binding_id: string
  matched_prompt_text: string
  prompt_snapshot: SunoPromptBaseSnapshot
}

export type SunoPromptTrackListResult = {
  prompt: SunoPrompt
  linked: SunoPromptTrackBindingSummary[]
  linkedTotal: number
  matching: SunoPromptTrackSummary[]
  matchingTotal: number
  page: number
  pageSize: number
}

export const getSunoPrompts = async (filters: SunoPromptFilters = {}): Promise<SunoPromptListResult> => {
  return requestJson<SunoPromptListResult>(
    `/api/admin/suno-prompts${buildQuery(filters)}`
  )
}

export const getSunoPromptGroups = async (search?: string): Promise<SunoPromptGroup[]> => {
  const payload = await requestJson<{ items: SunoPromptGroup[] }>(
    `/api/admin/suno-prompt-groups${buildQuery({ search })}`
  )

  return payload.items
}

export const getSunoPromptPools = async (): Promise<SunoPromptPool[]> => {
  const payload = await requestJson<{ items: SunoPromptPool[] }>('/api/admin/suno-prompt-pools')

  return payload.items
}

export const getSimilarSunoPrompts = async (promptId: string): Promise<SunoPromptSimilarityResult> =>
  requestJson<SunoPromptSimilarityResult>(
    `/api/admin/suno-prompts/${encodeURIComponent(promptId)}/similar`
  )

export const createSunoPromptPool = async (input: {
  canonicalPromptId: string
  description?: string | null | undefined
  name?: string | null | undefined
  promptIds: string[]
}): Promise<SunoPromptPool> => {
  const payload = await requestJson<{ item: SunoPromptPool }>('/api/admin/suno-prompt-pools', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateSunoPromptPool = async (input: {
  canonicalPromptId: string
  description?: string | null | undefined
  id: string
  name: string
  promptIds: string[]
}): Promise<SunoPromptPool> => {
  const payload = await requestJson<{ item: SunoPromptPool }>(
    `/api/admin/suno-prompt-pools/${encodeURIComponent(input.id)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        canonicalPromptId: input.canonicalPromptId,
        description: input.description,
        name: input.name,
        promptIds: input.promptIds
      })
    }
  )

  return payload.item
}

export const mergeSunoPromptPool = async (input: {
  canonicalPromptId: string
  sourcePoolId: string
  targetPoolId: string
}): Promise<SunoPromptPool> => {
  const payload = await requestJson<{ item: SunoPromptPool }>(
    `/api/admin/suno-prompt-pools/${encodeURIComponent(input.targetPoolId)}/merge`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        canonicalPromptId: input.canonicalPromptId,
        sourcePoolId: input.sourcePoolId
      })
    }
  )

  return payload.item
}

export const deleteSunoPromptPool = async (poolId: string): Promise<void> => {
  await requestJson(`/api/admin/suno-prompt-pools/${encodeURIComponent(poolId)}`, {
    method: 'DELETE'
  })
}

export const findMatchingSunoPrompt = async (
  stylePrompt: string,
  excludePrompt?: string | null | undefined
): Promise<SunoPrompt | null> => {
  const payload = await requestJson<{ item: SunoPrompt | null }>(
    `/api/admin/suno-prompts/match${buildQuery({ stylePrompt, excludePrompt: excludePrompt || undefined })}`
  )

  return payload.item
}

export const getSunoPrompt = async (promptId: string): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>(
    `/api/admin/suno-prompts/${encodeURIComponent(promptId)}`
  )

  return payload.item
}

export const getSunoPromptTracks = async (
  promptId: string,
  filters: { page?: number | undefined; pageSize?: number | undefined; search?: string | undefined } = {}
): Promise<SunoPromptTrackListResult> =>
  requestJson<SunoPromptTrackListResult>(
    `/api/admin/suno-prompts/${encodeURIComponent(promptId)}/tracks${buildQuery(filters)}`
  )

export const linkSunoPromptTracks = async (input: {
  linkAllMatching?: boolean
  promptId: string
  search?: string | undefined
  trackIds?: string[] | undefined
}): Promise<{ linkedCount: number; skippedCount: number }> =>
  requestJson<{ linkedCount: number; skippedCount: number }>(
    `/api/admin/suno-prompts/${encodeURIComponent(input.promptId)}/tracks/link`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        linkAllMatching: input.linkAllMatching,
        search: input.search,
        trackIds: input.trackIds
      })
    }
  )

export const unlinkAudioTrackSunoPrompt = async (trackId: string): Promise<void> => {
  const response = await fetch(
    `${apiUrl}/api/admin/audio-tracks/${encodeURIComponent(trackId)}/suno-prompt-binding`,
    {
      credentials: 'include',
      method: 'DELETE'
    }
  )

  if (!response.ok && response.status !== 404) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string }
    throw new Error(payload.message ?? `Request failed with status ${response.status}`)
  }
}

export const createSunoPrompt = async (input: {
  title: string
  description: string | null
  sunoGroupIds?: string[] | undefined
  style_prompt: string
  exclude_prompt: string | null
}): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>('/api/admin/suno-prompts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateSunoPrompt = async (input: {
  promptId: string
  title: string
  description: string | null
  sunoGroupIds?: string[] | undefined
  style_prompt: string
  exclude_prompt: string | null
}): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>(
    `/api/admin/suno-prompts/${encodeURIComponent(input.promptId)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        title: input.title,
        description: input.description,
        sunoGroupIds: input.sunoGroupIds,
        style_prompt: input.style_prompt,
        exclude_prompt: input.exclude_prompt
      })
    }
  )

  return payload.item
}

export const createSunoPromptGroup = async (input: {
  name: string
  description: string | null
}): Promise<SunoPromptGroup> => {
  const payload = await requestJson<{ item: SunoPromptGroup }>('/api/admin/suno-prompt-groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateSunoPromptGroup = async (input: {
  id: string
  name: string
  description: string | null
}): Promise<SunoPromptGroup> => {
  const payload = await requestJson<{ item: SunoPromptGroup }>(
    `/api/admin/suno-prompt-groups/${encodeURIComponent(input.id)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: input.name,
        description: input.description
      })
    }
  )

  return payload.item
}

export const deleteSunoPromptGroup = async (groupId: string): Promise<void> => {
  await requestJson(`/api/admin/suno-prompt-groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE'
  })
}

export const deleteSunoPrompt = async (promptId: string): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>(
    `/api/admin/suno-prompts/${encodeURIComponent(promptId)}`,
    {
      method: 'DELETE'
    }
  )

  return payload.item
}

export const suggestTrackTitles = async (input: {
  artist?: string | null
  language: TrackTitleLanguage
  style_prompt: string
}): Promise<TrackTitleSuggestionResult> =>
  requestJson<TrackTitleSuggestionResult>(
    '/api/admin/creative-tools/track-title-suggestions',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

export const suggestSunoStylePrompts = async (input: {
  description: string
  base_prompt_id?: string | null | undefined
  base_prompt?: SunoPromptBaseSnapshot | null | undefined
  generation_mode?: SunoStylePromptGenerationMode | null | undefined
}): Promise<SunoStylePromptSuggestionResult> =>
  requestJson<SunoStylePromptSuggestionResult>(
    '/api/admin/creative-tools/suno-style-prompts',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

export const suggestSunoPromptMetadata = async (input: {
  style_prompt: string
  exclude_prompt?: string | null | undefined
}): Promise<SunoPromptMetadataSuggestionResult> =>
  requestJson<SunoPromptMetadataSuggestionResult>(
    '/api/admin/creative-tools/suno-prompt-metadata',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

export const getSunoStylePromptHistory = async (): Promise<SunoStylePromptHistoryItem[]> => {
  const payload = await requestJson<{ items: SunoStylePromptHistoryItem[] }>(
    '/api/admin/creative-tools/suno-style-prompts/history'
  )

  return payload.items
}

export const deleteSunoStylePromptHistoryItem = async (historyId: string): Promise<void> => {
  await requestJson(
    `/api/admin/creative-tools/suno-style-prompts/history/${encodeURIComponent(historyId)}`,
    {
      method: 'DELETE'
    }
  )
}
